import { useEffect, useState } from 'react'
import axios from 'axios'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { optionalEmailSchema } from '@/lib/validations'
import { useMe } from '@/hooks/useMe'
import { useAuth } from '@/hooks/useAuth'
import { userService } from '@/services/user_service'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { ArrowLeft, Loader2 } from 'lucide-react'

const formSchema = z.object({
  user_name: z
    .string()
    .min(1, { message: '使用者名稱不可為空' })
    .max(50, { message: '使用者名稱不可超過 50 個字元' }),
  email: optionalEmailSchema(),
})

type FormValues = z.infer<typeof formSchema>

export default function Account() {
  const navigate = useNavigate()
  const { user, mutate } = useMe()
  const { token } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showEmailConfirm, setShowEmailConfirm] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<FormValues | null>(null)

  // 記錄初始 email 值，用於判斷是否已設定
  const [initialEmail, setInitialEmail] = useState<string | null>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      user_name: '',
      email: '',
    },
  })

  // 載入使用者資料
  useEffect(() => {
    if (user) {
      form.reset({
        user_name: user.user_name || '',
        email: user.email || '',
      })
      setInitialEmail(user.email || null)
    }
  }, [user, form])

  // 實際執行 API 更新
  const executeUpdate = async (data: FormValues) => {
    if (!token) {
      toast.error('未登入狀態')
      return
    }

    setIsSubmitting(true)
    try {
      const response = await userService.updateProfile(
        {
          user_name: data.user_name,
          // 只有當原本沒有 email 時才傳送
          email: initialEmail ? undefined : data.email || undefined,
        },
        token,
      )

      if (response.success) {
        await mutate()
        toast.success('個人資料更新成功')
      } else {
        toast.error(response.error?.message || '更新失敗')
      }
    } catch (error) {
      const message = axios.isAxiosError<{ error?: { message?: string } }>(error)
        ? error.response?.data?.error?.message
        : undefined
      toast.error(message || '發生錯誤，請稍後再試')
    } finally {
      setIsSubmitting(false)
    }
  }

  // 表單提交處理
  const onSubmit = async (data: FormValues) => {
    // 如果使用者有輸入新的 email（原本沒有 email 且現在有輸入）
    const isSettingNewEmail = !initialEmail && data.email && data.email.trim() !== ''

    if (isSettingNewEmail) {
      // 顯示確認對話框
      setPendingFormData(data)
      setShowEmailConfirm(true)
    } else {
      // 直接執行更新
      await executeUpdate(data)
    }
  }

  // 確認 email 設定後執行更新
  const handleConfirmEmail = async () => {
    setShowEmailConfirm(false)
    if (pendingFormData) {
      await executeUpdate(pendingFormData)
      setPendingFormData(null)
    }
  }

  // 取消 email 確認
  const handleCancelEmail = () => {
    setShowEmailConfirm(false)
    setPendingFormData(null)
  }

  // 格式化日期
  const formatDate = (dateString: string | Date | undefined) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
      {/* 背景裝飾 */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-br from-blue-100/30 to-cyan-100/30 rounded-full filter blur-3xl transform translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-teal-100/30 to-blue-100/30 rounded-full filter blur-3xl transform -translate-x-1/3 translate-y-1/3" />

      {/* 置中內容 */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <Button variant="ghost" className="mb-4" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回
          </Button>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>帳號資訊</CardTitle>
              <CardDescription>檢視與編輯您的個人資料</CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {/* 唯讀區塊 */}
                  <div className="space-y-4 pb-4 border-b">
                    <div>
                      <label className="text-sm font-medium text-gray-500">帳號</label>
                      <Input value={user?.account || ''} disabled className="mt-1 bg-gray-50" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">角色</label>
                      <Input
                        value={user?.role_type || ''}
                        disabled
                        className="mt-1 bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">建立時間</label>
                      <Input
                        value={formatDate(user?.created_at)}
                        disabled
                        className="mt-1 bg-gray-50"
                      />
                    </div>
                  </div>

                  {/* 可編輯區塊 */}
                  <FormField
                    control={form.control}
                    name="user_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>使用者名稱</FormLabel>
                        <FormControl>
                          <Input placeholder="請輸入使用者名稱" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder={initialEmail ? '' : '請輸入 Email'}
                            disabled={!!initialEmail}
                            className={initialEmail ? 'bg-gray-50' : ''}
                            {...field}
                          />
                        </FormControl>
                        {initialEmail ? (
                          <FormDescription>Email 設定後無法更改</FormDescription>
                        ) : (
                          <FormDescription className="text-amber-600">
                            注意：Email 一旦設定後將無法更改
                          </FormDescription>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex gap-4 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => navigate(-1)}
                    >
                      取消
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-gradient-to-r from-[#1698B6] to-[#0F7A8D]"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          儲存中...
                        </>
                      ) : (
                        '儲存變更'
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Email 確認對話框 */}
      <AlertDialog open={showEmailConfirm} onOpenChange={setShowEmailConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確認設定 Email</AlertDialogTitle>
            <AlertDialogDescription>
              您即將設定 Email 為：<strong>{pendingFormData?.email}</strong>
              <br />
              <br />
              <span className="text-amber-600 font-medium">
                請注意：Email 一旦設定後將無法更改，請確認輸入正確。
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelEmail}>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmEmail}>確認設定</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
